export enum RegEdFieldType {
	String = 'String',
	Text = 'Text',
	Integer = 'Integer',
	Decimal = 'Decimal',
	Boolean = 'Boolean',
	Money = 'Money',
	Date = 'Date',
	DateTime = 'DateTime',
	Time = 'Time',
	Email = 'Email',
	User = 'User',
	Contact = 'Contact',
	Option = 'Option',
	MultipleOptions = 'MultipleOptions',
	FullAddress = 'FullAddress',
	Note = 'Note',
	MeetingScheduler = 'MeetingScheduler',
	FileUpload = 'FileUpload',
	Label = 'Label',
}

export interface IRegEdMetadataOption {
	label?: string;
	externalId?: string;
	isActive?: boolean;
}

export interface IRegEdMetadataField {
	id?: string;
	externalId?: string;
	label?: string;
	helpText?: string;
	fieldType?: RegEdFieldType;
	isRequired?: boolean;
	isActive?: boolean;
	options?: IRegEdMetadataOption[];
	/** The polymorphic `_type`-discriminated `QuestionVisibility`; absent when the field is always shown. */
	visibleWhen?: Record<string, unknown> | null;
	order?: number;
}

export interface IRegEdMetadataForm {
	format?: string;
	displayName?: string;
	fields?: IRegEdMetadataField[];
}

export interface IRegEdMetadataFormImportResult {
	formsWritten?: number;
	questionsImported?: number;
	optionsImported?: number;
	warnings?: string[];
}

export interface IRegEdBrokerDealerSummary {
	id?: string;
	name?: string;
}

export enum RegEdConfigurationRequirement {
	Unknown = 'Unknown',
	BrokerDealer = 'BrokerDealer',
}

export interface IRegEdConfigurationStatus {
	needsConfiguration?: boolean;
	missingRequirements?: RegEdConfigurationRequirement[];
}

export interface IRegEdIntegrationStatus {
	id?: string;
	brokerDealerId?: string;
	/**
	 * Kill-switch: configured ≠ enabled. The account routes compliance through RegEd only when this is
	 * `true` AND `needsConfiguration` is `false`; only the enable/disable route flips it.
	 */
	enabled?: boolean;
	configurationStatus?: IRegEdConfigurationStatus;
	connectedAt?: string;
	connectedByUserId?: string;
}

export interface IRegEdConnectionTestResult {
	brokerDealerId?: string;
	success?: boolean;
	message?: string;
	testedAt?: string;
}

/** `answers` is keyed by `RegEdMetadataField.id`. */
export interface IRegEdSubmissionMetadata {
	format: string;
	answers: Record<string, unknown>;
}

export const buildRegEdSubmissionMetadata = (
	format: string,
	answers: Record<string, unknown>
): IRegEdSubmissionMetadata => ({ format, answers });

export enum RegEdComplianceContentType {
	Unknown = 'Unknown',
	Email = 'Email',
	Blog = 'Blog',
	SocialMediaPost = 'SocialMediaPost',
}

export interface IRegEdSubmissionCreateRequest {
	parentType: RegEdComplianceContentType;
	parentId: string;
	approvalRequestId: string;
	metadata: IRegEdSubmissionMetadata;
	revisesApprovalRequestId?: string | null;
}

export interface IRegEdUserMap {
	id?: string;
	levitateUserId?: string;
	regEdUserId?: string;
}

export interface IRegEdUserMapUpsertRequest {
	regEdUserId: string;
}

export const REGED_USER_ID_MAX_LENGTH = 24;

export enum RegEdSubmissionStatus {
	Pending = 'Pending',
	Submitting = 'Submitting',
	Approved = 'Approved',
	Rejected = 'Rejected',
	Withdrawn = 'Withdrawn',
	/** Submit failed, no tracking number — needs an operator retry. */
	Failed = 'Failed',
	/** Network failure mid-submit — may or may not have reached AdTrax. */
	Unknown = 'Unknown',
	/** No AdTrax decision within the SLA window. */
	Expired = 'Expired',
}

export enum RegEdApprovalStatus {
	Rejected = 'Rejected',
	Approved = 'Approved',
}

/** AdTrax decision code that gates the revise-and-resubmit flow (see `decisionCode`). */
export const REGED_REVISABLE_DECISION_CODE = 'Revise and Resubmit';

/** Rows are submission *chains* (a revision repoints the same row), returned newest-first. */
export interface IRegEdSubmissionView {
	id?: string;
	creationDate?: string;
	lastModifiedDate?: string;
	approvalRequestId?: string;
	revisesApprovalRequestId?: string | null;
	contentType?: RegEdComplianceContentType;
	parentId?: string;
	brokerDealerId?: string;
	status?: RegEdSubmissionStatus;
	approvalStatus?: RegEdApprovalStatus | null;
	trackingNumber?: string | null;
	decisionCode?: string | null;
	metadata?: IRegEdSubmissionMetadata;
}
